import { fetchCaptchaData } from '@eliadmin/core/src/api/recaptcha-fetcher';
import { CaptchaAction } from '@eliadmin/core/src/api/recaptcha-actions';
import type {
  GenericRequest,
  PaginationMeta,
  PaginationQuery,
} from '@eliadmin/core/src/api/api-types';
import { fetchData } from '@eliadmin/core/src/api/fetcher';
import { fetchSlugModel } from '@eliadmin/core/src/api/slugs/fetch-slug-model';
import type { FetchSlugDetailQuery } from '@eliadmin/core/src/api/slugs/slugs-api';
import type {
  HousingAvailabilityRequest,
  HousingAvailabilityResponse,
  HousingNavLinks,
  HousingProperty,
  HousingPropertyDetail,
  HousingPropertiesAvailableRequest,
  HousingReservationRequest,
  HousingReservationResponse,
  HousingRoom,
  HousingContentDefinition,
  HousingType,
} from './housing-types';

export type FetchHousingPropertiesQuery = PaginationQuery;

export async function fetchHousingProperties({
  locale,
  query,
}: GenericRequest<{ query?: FetchHousingPropertiesQuery }>) {
  return fetchData<{ data: HousingProperty[]; links: HousingNavLinks; meta: PaginationMeta }>({
    uri: '/housing/properties',
    method: 'GET',
    locale,
    query,
  });
}

export async function fetchHousingProperty({
  locale,
  slug,
  query,
}: GenericRequest<{ slug: string; query?: FetchSlugDetailQuery }>) {
  const { data } = await fetchSlugModel({
    model: 'App\\Models\\Housing\\HousingProperty',
    locale,
    slug,
    query,
  });

  return { data: data as HousingPropertyDetail };
}

export type FetchHousingRoomsQuery = PaginationQuery & {
  'where[property_id][equals]'?: number;
};

export async function fetchHousingRooms({
  locale,
  query,
}: GenericRequest<{ query?: FetchHousingRoomsQuery }>) {
  return fetchData<{ data: HousingRoom[]; links: HousingNavLinks; meta: PaginationMeta }>({
    uri: '/housing/rooms',
    method: 'GET',
    locale,
    query,
  });
}

export async function fetchHousingContentDefinitions({ locale }: GenericRequest) {
  return fetchData<{ data: HousingContentDefinition[] }>({
    uri: '/housing/text-fields',
    method: 'GET',
    locale,
  });
}

export async function fetchHousingTypes({
  locale,
  query,
}: GenericRequest<{ query?: PaginationQuery }>) {
  return fetchData<{ data: HousingType[]; links: HousingNavLinks; meta: PaginationMeta }>({
    uri: '/housing/types',
    method: 'GET',
    locale,
    query,
  });
}

export async function checkHousingAvailability({
  locale,
  body,
}: GenericRequest<{ body: HousingAvailabilityRequest }>) {
  return fetchData<HousingAvailabilityResponse>({
    uri: '/housing/availability',
    method: 'POST',
    locale,
    body,
  });
}

export async function fetchAvailableHousingProperties({
  locale,
  body,
}: GenericRequest<{ body: HousingPropertiesAvailableRequest }>) {
  return fetchData<{ data: HousingProperty[] }>({
    uri: '/housing/properties/available',
    method: 'POST',
    locale,
    body,
  });
}

export async function createHousingReservation({
  locale,
  body,
}: GenericRequest<{ body: HousingReservationRequest }>) {
  return fetchCaptchaData<HousingReservationResponse>(
    {
      uri: '/housing/reservations',
      method: 'POST',
      locale,
      body,
    },
    CaptchaAction.HOUSING_RESERVATION,
  );
}
